import { CONNECTORS, type ConnectorMeta } from '@research-assistant/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SearchIcon } from 'lucide-react';
import {
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from 'react';

import { Input } from '@/components/ui/input.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { workspaceQueryOptions } from '@/lib/api/workspaces.js';
import { getRuntimeConfig, loadRuntimeConfig } from '@/lib/runtime-config.js';
import { cn } from '@/lib/utils.js';

import { useConnectorPresence } from '../../hooks/useConnections.js';
import { useServerEvent } from '../../runtime/server-events.js';
import * as api from '../../services/integration-api.js';
import type { ConnectorPresence } from '../../services/integration-api.js';

import { ConnectorCard } from './ConnectorCard.js';
import { useConnectorScope } from './connector-scope.js';

/** BrowserStack's catalog entry, for deployments whose catalog does not list it yet. */
export const BROWSERSTACK_CONNECTOR: ConnectorMeta = {
  id: 'browserstack',
  name: 'BrowserStack',
  vendor: 'BrowserStack',
  authKind: 'api-key',
  description:
    'Run automated and manual tests across the BrowserStack real-device and browser cloud.',
  source: 'builtin',
};

/**
 * The connectors directory body — search box + card grid — shared by the chat "+" modal and the
 * workspace manage tab. Reads its workspace from the surrounding `ConnectorScopeProvider`, so the
 * same grid renders the chat's selected workspace or the manage tab's managed workspace with no
 * per-surface branching. The dialog shell (top bar, category rail) lives only in the modal wrapper.
 *
 * `variant` controls scrolling: `'modal'` (default) is its own scroll container inside the
 * fixed-height dialog; `'page'` flows naturally so the page scrolls. A `'modal'` layout inside a
 * page would trap the wheel in a dead internal scroller (`overscroll-contain` with nothing to
 * scroll), so the manage tab passes `'page'`.
 *
 * The cards come out in two named sections, connected first, because most of the catalogue is a
 * shopping list and the caller's own connections would otherwise sit scattered through it. The
 * headings appear only while both sections hold something — one section on screen needs no label.
 */
export function ConnectorsDirectory({
  variant = 'modal',
  connectOnOpen,
}: {
  variant?: 'modal' | 'page';
  /**
   * A provider whose connect flow should start as soon as this grid renders, sent by a surface that
   * offers the connect but does not own the flow — the Knowledge tab's connections dialog is one.
   * Only the provider's FIRST card acts on it: a provider can hold several connections here, and
   * every one of their cards offers the same "add another".
   */
  connectOnOpen?: string;
} = {}): JSX.Element {
  const { workspaceId } = useConnectorScope();
  const isModal = variant === 'modal';
  const [query, setQuery] = useState('');
  // Bumped whenever the runtime-config connector flags are refreshed (on
  // `connector.changed`), so the kill-switch-filtered `source` memo recomputes
  // even when the catalog data is structurally unchanged.
  const [configNonce, setConfigNonce] = useState(0);
  const [pendingRefreshes, setPendingRefreshes] = useState(0);
  const qc = useQueryClient();
  const {
    data: presence,
    isLoading: statusLoading,
    isFetching: statusFetching,
    isError: statusError,
  } = useConnectorPresence(workspaceId);
  const powerBiPresence = useRef<{
    workspaceId: string | null;
    snapshot?: ConnectorPresence;
  }>({ workspaceId });
  if (powerBiPresence.current.workspaceId !== workspaceId) {
    powerBiPresence.current = { workspaceId };
  }
  if (presence && !presence.readFailed) {
    powerBiPresence.current.snapshot = presence;
  }
  const powerBiConnectDisabled =
    pendingRefreshes > 0 ||
    !presence ||
    presence.readFailed === true ||
    statusFetching ||
    statusError;

  const {
    data: workspace,
    isError: workspaceError,
    isFetching: workspaceFetching,
  } = useQuery({
    ...workspaceQueryOptions(workspaceId ?? ''),
    enabled: Boolean(workspaceId),
  });
  const qualityEnabled =
    !workspaceFetching &&
    !workspaceError &&
    (workspace?.domains?.find((domain) => domain.domain === 'quality')
      ?.enabled ??
      false);

  // v2 catalog seam — see ConnectorCatalogService on the server. Authoritative refresh comes from
  // SSE (`connector.changed` below); the staleTime + refetchOnMount are the fallback for clients
  // that lose the SSE channel (offline, sleeping tab) so the directory still shows current state on
  // the next open. The catalog is provider-level metadata (workspace-agnostic); connectedness is
  // scoped per workspace via `useConnections`.
  const { data: catalog, isLoading: catalogLoading } = useQuery({
    queryKey: ['connectors-catalog'],
    queryFn: api.getConnectorsCatalog,
    staleTime: 10_000,
    refetchOnMount: 'always',
    placeholderData: [...CONNECTORS],
  });

  // Live refresh: when an admin toggles a connector kill-switch from any device, the server
  // broadcasts `connector.changed`; we invalidate the catalog query and re-render with the new state.
  useServerEvent(
    'connector.changed',
    useCallback(() => {
      setPendingRefreshes((count) => count + 1);
      void loadRuntimeConfig(true)
        .finally(async () => {
          setConfigNonce((nonce) => nonce + 1);
          await qc.invalidateQueries({ queryKey: ['connectors-catalog'] });
          await qc.invalidateQueries({ queryKey: ['connections'] });
        })
        .finally(() => setPendingRefreshes((count) => count - 1));
    }, [qc]),
  );

  const isLoading = catalogLoading || statusLoading;
  /** Power BI requires an explicit enabled flag; existing connector defaults stay unchanged. */
  const source = useMemo<ConnectorMeta[]>(() => {
    const connectorFlags = getRuntimeConfig().connectors;
    const base = catalog && catalog.length > 0 ? catalog : [...CONNECTORS];
    const visible = base.filter(
      (connector) =>
        (connector.id === 'powerbi'
          ? connectorFlags.powerbi === true
          : connectorFlags[connector.id] !== false) &&
        (connector.id !== 'browserstack' || qualityEnabled),
    );
    if (
      qualityEnabled &&
      connectorFlags.browserstack !== false &&
      !visible.some((connector) => connector.id === 'browserstack')
    ) {
      return [...visible, BROWSERSTACK_CONNECTOR];
    }
    return visible;
    // configNonce: recompute when the runtime-config flags are refreshed live.
  }, [catalog, configNonce, qualityEnabled]);

  const filtered = useMemo<ConnectorMeta[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [...source];
    return source.filter(
      (connector) =>
        connector.name.toLowerCase().includes(q) ||
        connector.vendor.toLowerCase().includes(q) ||
        connector.description.toLowerCase().includes(q),
    );
  }, [query, source]);

  /**
   * The cards, split by whether the caller already reaches the provider. Both lists keep catalogue
   * order and every provider keeps the server's resolution order inside them, so each card renders
   * where it did before the split — only under a heading.
   */
  const connectedCards: JSX.Element[] = [];
  const availableCards: JSX.Element[] = [];
  for (const meta of filtered) {
    const cardPresence =
      meta.id === 'powerbi' ? powerBiPresence.current.snapshot : presence;
    const entry = cardPresence?.providers[meta.id];
    const cards = connectorCards(
      meta,
      cardPresence,
      meta.id === connectOnOpen,
      meta.id === 'powerbi' && powerBiConnectDisabled,
    );
    const isConnected =
      (entry?.connections.length ?? 0) > 0 || Boolean(entry?.connected);
    if (isConnected) {
      connectedCards.push(...cards);
    } else {
      availableCards.push(...cards);
    }
  }

  return (
    <div className={cn('flex flex-col', isModal && 'min-h-0 flex-1')}>
      <div className="px-4 pt-4 md:px-6">
        <div className="relative">
          <SearchIcon
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            placeholder="Search connectors…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search connectors"
            className="pl-9"
          />
        </div>
      </div>

      <section
        className={cn(
          'flex flex-col gap-3 px-4 pt-4 pb-5 md:px-6',
          isModal && 'min-h-0 flex-1 overflow-y-auto overscroll-contain',
        )}
      >
        {isLoading ? (
          <CardGrid>
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </CardGrid>
        ) : filtered.length === 0 ? (
          <CardGrid>
            <p className="col-span-full py-8 text-center text-sm text-muted-foreground">
              No connectors match “{query}”.
            </p>
          </CardGrid>
        ) : (
          <>
            <CardSection
              title="Connected"
              cards={connectedCards}
              showHeading={availableCards.length > 0}
            />
            <CardSection
              title="Available"
              cards={availableCards}
              showHeading={connectedCards.length > 0}
            />
          </>
        )}
      </section>
    </div>
  );
}

/**
 * The cards one catalogue entry contributes: one per connection the caller holds, or a single card
 * offering the connect when they hold none.
 *
 * Every connection gets a card, for every provider. There used to be a list of the providers whose
 * consumers can reach a NAMED connection, and only those drew more than one card — everywhere else the
 * flat provider key of the token resolve answered from whichever connection identity resolved first, so
 * a second card would have carried a green pill over a connection nothing asked anything. That list is
 * gone: the presence list now holds only the CALLER's own connections, so hiding the second one hides a
 * connection they made themselves, which is the whole feature. Selecting per connection everywhere is
 * what the `X-Connection-Id` header lands next.
 */
function connectorCards(
  meta: ConnectorMeta,
  presence: ConnectorPresence | undefined,
  /** Whether this provider's first card should start a connect as soon as it mounts. */
  connectOnOpen = false,
  connectDisabled = false,
): JSX.Element[] {
  const entry = presence?.providers[meta.id];
  const connections = entry?.connections ?? [];
  const externalTenantLaneAvailable =
    presence?.externalTenantLaneAvailable ?? false;
  const approvedTenants = presence?.externalTenantConsents ?? [];
  /**
   * Nothing connected — one card offering the connect. This is also the shape a server too old to
   * send `connections` produces, which keeps the grid readable rather than empty.
   */
  if (connections.length === 0) {
    return [
      <ConnectorCard
        key={meta.id}
        meta={meta}
        connected={Boolean(entry?.connected)}
        connectedVia={entry?.resolvedFrom ?? null}
        permissionLevel={entry?.permissionLevel ?? null}
        externalTenantLaneAvailable={externalTenantLaneAvailable}
        approvedExternalTenants={approvedTenants}
        connectOnOpen={connectOnOpen}
        connectDisabled={connectDisabled}
      />,
    ];
  }
  return connections.map((connection, index) => (
    <ConnectorCard
      key={`${meta.id}:${connection.connectionId}`}
      meta={meta}
      connected
      connectedVia={connection.scope}
      permissionLevel={connection.permissionLevel}
      connection={connection}
      peerConnections={connections}
      offersAnotherConnection
      externalTenantLaneAvailable={externalTenantLaneAvailable}
      approvedExternalTenants={approvedTenants}
      connectOnOpen={connectOnOpen && index === 0}
      connectDisabled={connectDisabled}
    />
  ));
}

/**
 * One named group of cards: a quiet heading carrying its card count, then the group's own grid. The
 * heading is a real `h2` above the cards' `h3` titles, and it names the group through
 * `aria-labelledby` so a screen reader announces which set it has entered.
 */
function CardSection({
  title,
  cards,
  showHeading,
}: {
  title: string;
  cards: JSX.Element[];
  showHeading: boolean;
}): JSX.Element | null {
  const headingId = useId();
  if (cards.length === 0) return null;
  return (
    <div
      role={showHeading ? 'group' : undefined}
      aria-labelledby={showHeading ? headingId : undefined}
      className="flex flex-col gap-3"
    >
      {showHeading && (
        <h2
          id={headingId}
          className="text-mini font-semibold uppercase tracking-wider text-muted-foreground"
        >
          {title} ({cards.length})
        </h2>
      )}
      <CardGrid>{cards}</CardGrid>
    </div>
  );
}

/** The two-column card grid every state renders into — cards, skeletons, or the empty-search note. */
function CardGrid({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
  );
}

function CardSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card/40 p-4">
      <div className="flex items-center gap-3">
        <Skeleton className="size-10 rounded-lg" />
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-2.5 w-16" />
        </div>
      </div>
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-3/4" />
    </div>
  );
}
